import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { FormService } from '../form.service';

type GetFormsParams = Forms.ListFormsAdminParams;

export const useGetForms = (
	params?: GetFormsParams,
	options?: Omit<
		UseQueryOptions<Forms.PaginatedAdminForms, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['forms', params],
		queryFn: () => FormService.getAll(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetForm = (
	id: string,
	options?: Omit<
		UseQueryOptions<Forms.FormAdminDetail, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['form', id],
		queryFn: () => FormService.getById(id),
		refetchOnWindowFocus: false,
		...options,
	});
};

interface GetAvailableFormsParams {
	page?: number;
	limit?: number;
}

export const useGetAvailableForms = (
	params?: GetAvailableFormsParams,
	options?: Omit<
		UseQueryOptions<Forms.PaginatedAvailableForms, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['forms', 'available', params],
		queryFn: () => FormService.getAvailable(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

interface GetAvailableCompanyFormsParams {
	page?: number;
	limit?: number;
}

/** Portal `/empresa` — formulários vinculados à empresa logada. */
export const useGetAvailableFormsForCompany = (
	params?: GetAvailableCompanyFormsParams,
	options?: Omit<
		UseQueryOptions<Forms.PaginatedCompanyForms, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['forms', 'available', 'company', 'me', params],
		queryFn: () => FormService.getAvailableForCompany(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

/** Após login do funcionário no formulário empresa. */
export const useGetAvailableFormsForEmployee = (
	params?: GetAvailableFormsParams,
	options?: Omit<
		UseQueryOptions<Forms.PaginatedAvailableForms, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['forms', 'available', 'employee', 'me', params],
		queryFn: () => FormService.getAvailableForEmployee(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetFormMyAnswers = (
	formId: string,
	options?: Omit<
		UseQueryOptions<Forms.FormAnswer[], Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['form', formId, 'my-answers'],
		queryFn: () => FormService.getMyAnswers(formId),
		enabled: !!formId,
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetMyFormAnswersAnalytics = (
	options?: Omit<
		UseQueryOptions<Forms.FormMyAnswersAnalytics, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['forms', 'answers', 'me', 'analytics'],
		queryFn: () => FormService.getMyAnswersAnalytics(),
		refetchOnWindowFocus: false,
		...options,
	});
};

interface GetFormAnswersParams {
	page?: number;
	limit?: number;
}

export const useGetFormAnswers = (
	formId: string,
	params?: GetFormAnswersParams,
	options?: Omit<
		UseQueryOptions<Forms.PaginatedFormAnswersAdmin, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['form', formId, 'answers', params],
		queryFn: () => FormService.getAnswers(formId, params),
		enabled: !!formId,
		refetchOnWindowFocus: false,
		...options,
	});
};

interface GetFormApplicationPeriodsParams {
	page?: number;
	limit?: number;
	activeNow?: boolean;
}

export const useGetFormApplicationPeriods = (
	formId: string,
	companyId: string,
	params?: GetFormApplicationPeriodsParams,
	options?: Omit<
		UseQueryOptions<Forms.PaginatedFormApplicationPeriods, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: [
			'form',
			formId,
			'company',
			companyId,
			'application-periods',
			params,
		],
		queryFn: () =>
			FormService.getApplicationPeriods(formId, companyId, params),
		enabled: !!formId && !!companyId,
		refetchOnWindowFocus: false,
		...options,
	});
};

export function isFormAnalyticsPayload(
	response: Forms.FindFormAnalyticsResponse,
): response is Forms.FormAnalytics {
	return (
		typeof response === 'object' && response !== null && 'id' in response
	);
}

export const useGetFormAnalytics = (
	formId: string,
	params: Forms.FindFormAnalyticsQueryParams,
	options?: Omit<
		UseQueryOptions<Forms.FindFormAnalyticsResponse, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['form', formId, 'analytics', params],
		queryFn: () => FormService.getAnalytics(formId, params),
		enabled: !!formId && !!params.companyId,
		refetchOnWindowFocus: false,
		...options,
	});
};
